import { useState } from "react";
import { useOutletContext } from "react-router-dom";

import Navbar from "../../components/navbar/navbar";
import Footer from "../../components/footer/footer";
import HelmetSEO from "../../components/helmetSeo/helmetSeo";
import Pagination from "../../components/pagination/Pagination";
import OneColumnArticleListingLayout from "../../components/articleListingLayouts/oneColumnArticleListingLayout/oneColumnArticleListingLayout";
import TwoColumnArticleListingLayout from "../../components/articleListingLayouts/twoColumnArticleListingLayout/twoColumnArticleListingLayout";
import RichTextWithBlokRenderer from "../../components/richTextRenderer/richTextRenderer";
import ListingHeader from "../../components/listingHeader/listingHeader";
import LowerBannerPanel from "../../bloks/panels/lowerBannerPanel/lowerBannerPanel";

import GetComponentsFromBloks from "../../helpers/bloksRenderer";
import { useCurrentPageSearchParam } from "../../hooks/usePagination";
import { useCategoryArticles } from "../../hooks/useStoryblokArticles";
import { storyblokStoryUuidToUrl } from "../../helpers/URLHelpers";
import { GetWindowLanguage } from "../../helpers/storyHelpers";

import styles from "./category.module.scss";
import PageContentWrapper from "../../components/PageContentWrapper";
import RssFeedLink from "../../features/seo/rssFeedLink";

const PER_PAGE = 16;
const FEATURED = 6;

function CategoryLayout() {
  const { story } = useOutletContext();
  const locale = GetWindowLanguage();

  const lang = locale || "en";
  const pagePath = storyblokStoryUuidToUrl(story?.uuid ?? null);
  const currentPageSearchParam = useCurrentPageSearchParam();
  const [currentPage, setCurrentPage] = useState(currentPageSearchParam || 1);
  const [currentArticles, totalArticles, isLoading] = useCategoryArticles(
    story?.content?.title,
    PER_PAGE,
    currentPage
  );

  function handlePageChange(newPage) {
    if (newPage !== currentPage) {
      setCurrentPage(newPage);

      const url = new URL(window.location);
      if (newPage === 1) {
        url.searchParams.delete("page");
      } else {
        url.searchParams.set("page", newPage);
      }
      window.history.pushState({}, "", url);
      window.scrollTo(0, 0);
    }
  }

  let featuredArticles = [],
    listArticles = [];

  if (currentPage === 1) {
    if (currentArticles.length < FEATURED) {
      listArticles = currentArticles;
      featuredArticles = [];
    } else {
      featuredArticles = currentArticles.slice(0, FEATURED);
      listArticles = currentArticles.slice(FEATURED);
    }
  } else {
    listArticles = currentArticles;
  }

  let aboveListingComponents = [];

  if (story?.content?.above_listing_bloks) {
    aboveListingComponents = GetComponentsFromBloks(
      story.content.above_listing_bloks,
      story,
    );
  }

  let belowListingComponents = [];

  if (story?.content?.below_listing_bloks) {
    belowListingComponents = GetComponentsFromBloks(
      story.content.below_listing_bloks,
      story
    );
  }

  let asideComponentList = [];

  if (story?.content?.aside) {
    asideComponentList = GetComponentsFromBloks(story.content.aside, story);
  }

  return (
    <>
      <HelmetSEO
        lang={lang}
        page_path={pagePath}
        story_alternates={story?.content?.alternates ?? []}
        seo_data={story?.content?.seo ?? {}}
        fallback_title={story?.content?.title ?? ""}
      />
      <RssFeedLink />
      <Navbar />
      <PageContentWrapper>
        <div className={`${styles.view}`}>
          <div className={styles.aboveListingComponents}>
            {aboveListingComponents}
          </div>
          <div className={`${styles.mainlayout} page-content`}>
            <main className={styles.mainpage}>
              {story?.content?.show_headline ? (
                <>
                  <ListingHeader
                    withClass={styles.listingheader}
                    underlineColor="backgroundcolor-crscooter"
                  >
                    <h2>{story.content.title}</h2>
                  </ListingHeader>
                  {story.content.description ? (
                    <div className={styles.desc}>
                      <RichTextWithBlokRenderer
                        document={story.content.description}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              {isLoading ? <div className="loader centered" /> : null}
              {currentPage === 1 && featuredArticles.length > 0 ? (
                <TwoColumnArticleListingLayout articles={featuredArticles} />
              ) : null}
              {story?.content?.ads?.lower_banner_panel && currentPage === 1 ? (
                <LowerBannerPanel
                  blok={{ margin_top: "default", margin_bottom: "default" }}
                  lower_banner_panel={story.content.ads.lower_banner_panel}
                />
              ) : null}
              {listArticles.length > 0 ? (
                <OneColumnArticleListingLayout articles={listArticles} />
              ) : null}
              {totalArticles > 0 ? (
                <Pagination
                  currentPage={currentPage}
                  totalCount={totalArticles}
                  pageSize={PER_PAGE}
                  onPageChange={handlePageChange}
                />
              ) : null}
              {belowListingComponents}
            </main>
            <aside className={styles.asidepage}>
              <div className={styles.asidecontent}>{asideComponentList}</div>
            </aside>
          </div>
        </div>
      </PageContentWrapper>
      <Footer />
    </>
  );
}

export default CategoryLayout;
